import React, { createContext, useState, useContext, useCallback } from 'react';

const WorkflowStateContext = createContext(undefined); // Initialize as undefined
const WorkflowDispatchContext = createContext(undefined); // Initialize as undefined

const initialState = {
  currentStep: 0, // 0: Login, 1: Plans, 2: Personalize Step 1 (URL), etc.
  user: null, // Store user info after login
  selectedPlan: null, // e.g., 'trial', 'basic', 'pro', 'enterprise'
  websiteUrl: '',
  uploadedDocs: [], // Array of file objects or metadata
  chatbotType: 'text', // 'text' or 'phone'
  selectedPhoneNumber: null, // Store selected Twilio number
  selectedVoice: null, // Store selected voice { id: '...', name: '...', file: '...' }
  isComplete: false,
};

export const WorkflowProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const setWorkflowUser = useCallback((user) => {
    setState(prev => ({ ...prev, user, currentStep: 1 })); // Move to plans after login
  }, []);

  const selectPlan = useCallback((planId) => {
    // Reset dependent steps if plan changes mid-workflow
    setState(prev => ({
        ...prev,
        selectedPlan: planId,
        chatbotType: 'text', // Default back to text
        selectedPhoneNumber: null,
        selectedVoice: null,
        currentStep: 2 // Move to personalize step 1 (URL)
    }));
  }, []);

  const setPersonalizationData = useCallback((data) => {
    // Ensure we merge data correctly
    setState(prev => ({ ...prev, ...data }));
  }, []);

  const nextStep = useCallback(() => {
     // Logic for skipping steps might be better handled in the navigation function
     // within PersonalizePage to keep context simpler.
    setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const previousStep = useCallback(() => {
     // Logic for skipping steps might be better handled in the navigation function
     // within PersonalizePage.
    setState(prev => ({ ...prev, currentStep: Math.max(0, prev.currentStep - 1) }));
  }, []);

  const completeWorkflow = useCallback(() => {
      // Here you would typically send all collected data (state) to your backend
      console.log("Workflow Complete! Sending data:", state);
      setState(prev => ({ ...prev, isComplete: true, currentStep: prev.currentStep + 1 })); // Increment step to move past final step
       // Navigation to success page happens in the component calling this
  }, [state]); // Depend on state so the logged data is current


  const resetWorkflow = useCallback(() => {
    setState(initialState);
    // Consider navigating back to home or login here if needed
  }, []);

  return (
    <WorkflowStateContext.Provider value={state}>
      <WorkflowDispatchContext.Provider value={{
        setWorkflowUser,
        selectPlan,
        setPersonalizationData,
        nextStep, // Basic step increment
        previousStep, // Basic step decrement
        completeWorkflow,
        resetWorkflow
      }}>
        {children}
      </WorkflowDispatchContext.Provider>
    </WorkflowStateContext.Provider>
  );
};

export const useWorkflowState = () => {
  const context = useContext(WorkflowStateContext);
  if (context === undefined) {
    throw new Error('useWorkflowState must be used within a WorkflowProvider');
  }
  return context;
};

export const useWorkflowDispatch = () => {
  const context = useContext(WorkflowDispatchContext);
  if (context === undefined) {
    throw new Error('useWorkflowDispatch must be used within a WorkflowProvider');
  }
  return context;
};
